/**
 * Link repository (spec UI §2): a repository, the account keys its project builds into (Connected keys
 * only), and the project path. One `warehouseProjects.link` call creates the project with every key.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { accountKeyOptions, type LinkedProject, type LinkedRepoAccount } from '../../lib/linkedRepoList';
import { parseRepoKey, type Repo } from '../repo-utils';
import { RepoCombobox } from '../RepoCombobox';
import { AccountKeyPicker } from './AccountKeyPicker';
import {
  linkRepositorySchema,
  type LinkRepositoryValues,
  normalizeProjectPath,
  rpcErrorCode,
  rpcErrorMessage,
} from './linkedRepoSchema';
import { useInvalidateLinkedRepos } from './useInvalidateLinkedRepos';

const DEFAULTS: LinkRepositoryValues = { repoKey: '', accountKeys: [], projectPath: '/' };

export function LinkRepositoryDialog({
  open,
  onOpenChange,
  repos,
  accounts,
  projects,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repos: readonly Repo[];
  accounts: readonly LinkedRepoAccount[];
  projects: readonly LinkedProject[];
}): React.JSX.Element {
  const orpc = useOrpc();
  const invalidate = useInvalidateLinkedRepos();
  const form = useForm<LinkRepositoryValues>({
    resolver: zodResolver(linkRepositorySchema),
    mode: 'onTouched',
    defaultValues: DEFAULTS,
  });
  const link = useMutation(orpc.warehouseProjects.link.mutationOptions());
  const pending = link.isPending;

  useEffect(() => {
    if (open) {
      form.reset(DEFAULTS);
      link.reset();
    }
    // Reset only on open; `form` and `link` are stable for the dialog's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on open; the form and mutation are stable
  }, [open]);

  // Connected keys only: a key with no account has nothing to build docs from.
  const options = useMemo(() => accountKeyOptions(accounts), [accounts]);
  const repoKey = useWatch({ control: form.control, name: 'repoKey' });
  const linkedAt = useMemo(() => {
    if (!repoKey) return [];
    const { repoFullName } = parseRepoKey(repoKey);
    return projects
      .filter((p) => p.repoFullName.toLowerCase() === repoFullName.toLowerCase())
      .map((p) => p.projectPath);
  }, [projects, repoKey]);

  const submit = form.handleSubmit((values) => {
    const parsed = parseRepoKey(values.repoKey);
    link.mutate(
      {
        repoFullName: parsed.repoFullName,
        installationId: Number.isFinite(parsed.installationId) ? parsed.installationId : 0,
        projectPath: normalizeProjectPath(values.projectPath),
        accountKeys: values.accountKeys,
        provider: 'github',
      },
      {
        onSuccess: () => {
          invalidate();
          toast.success('Repository linked. Docs are being generated.');
          onOpenChange(false);
        },
        onError: (err) => {
          const code = rpcErrorCode(err);
          if (code === 'CONFLICT') {
            form.setError('projectPath', { message: 'Already linked. Edit it instead.' });
          } else if (code === 'BAD_REQUEST' && /path/i.test(err.message)) {
            form.setError('projectPath', { message: err.message });
          } else {
            form.setError('root', { message: rpcErrorMessage(err, 'Could not link the repository.') });
          }
        },
      },
    );
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        closeDisabled={pending}
        onEscapeKeyDown={(e) => {
          if (pending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Link repository</DialogTitle>
          <DialogDescription>
            Lookout builds docs from the project for each account key you pick, and chat reads its code when it answers
            about those databases.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            id="link-repository"
            noValidate
            onSubmit={(e) => void submit(e)}
            className="flex min-w-0 flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="repoKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Repository</FormLabel>
                  <FormControl>
                    <RepoCombobox
                      repos={[...repos]}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select repository…"
                      disabled={pending}
                    />
                  </FormControl>
                  <FormDescription>
                    {linkedAt.length > 0
                      ? `Linked at ${linkedAt.join(', ')}. Link it once per project path.`
                      : 'A repository can hold several projects. Link it once per project path.'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="projectPath"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project path</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="/" disabled={pending} />
                  </FormControl>
                  <FormDescription>
                    The folder that holds the project file. Use / for the repository root.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="accountKeys"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account keys</FormLabel>
                  <FormDescription>
                    Pick the databases this project builds into. You can pick more than one, for example PROD and DEV.
                  </FormDescription>
                  <FormControl>
                    <AccountKeyPicker
                      options={options}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={pending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {rootError && <Callout variant="error">{rootError}</Callout>}
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => onOpenChange(false)}
            label="Cancel"
          />
          <Button
            type="submit"
            form="link-repository"
            variant="primary"
            loading={pending}
            label={pending ? 'Linking…' : 'Link repository'}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
